import React, { useState, useEffect, useMemo } from 'react'
import { usePlayerHistory, type PlayerHistory, type BattleRecord } from '../hooks/usePlayerHistory'
import { usePlayerProgression } from '../hooks/usePlayerProgression'
import { useBlockchainStats } from '../hooks/useBlockchainStats'
import { buildBackendUrl } from '../utils/backendUrl'
import type { ActivePage } from './MainMenu'
import type { BottomNavKey } from './BottomNav'
import { Flame, Sparkles, ChevronDown, X } from 'lucide-react'
import { useAccount } from 'wagmi'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { PlayerProgressionPanel } from './PlayerProgressionPanel'
import { formatRarityLabel, getRarityBadgeClasses } from '../utils/rarityBadges'

// AGC: Battle log data structure from backend JSON
interface BattleLogTurn {
  turn: number
  player1: {
    choice?: string
    element?: string
    damage?: number
    energy?: number
    energyAfter?: number // AGC: Energy after this turn
    activatedBuffs?: string[]
    buffs?: number[] // AGC: Selected buff IDs for this turn
    buffStatus?: Array<{ name: string; status: string }> // AGC: Buff status (activated/failed)
    potions?: number[] // AGC: Potions used this turn
    frostUsed?: boolean // AGC: Whether frost was used
    breakdown?: TurnBreakdown
  }
  player2: {
    choice?: string
    element?: string
    damage?: number
    energy?: number
    energyAfter?: number // AGC: Energy after this turn
    activatedBuffs?: string[]
    buffs?: number[] // AGC: Selected buff IDs for this turn
    buffStatus?: Array<{ name: string; status: string }> // AGC: Buff status (activated/failed)
    potions?: number[] // AGC: Potions used this turn
    frostUsed?: boolean // AGC: Whether frost was used
    breakdown?: TurnBreakdown
  }
}

interface TurnBreakdown {
  baseName?: string
  baseDamage?: number
  levelBonus?: number
  elementBonus?: number
  elementName?: string
  rarityBonus?: number
  rarityName?: string
  hasteBonus?: number
  rawDamage?: number
  defense?: number
  afterDefense?: number
  pierceReduction?: number
  shieldReduction?: number
  frostReduction?: number
  finalDamage?: number
  manaBoost?: number
  potionHealing?: number
  potionPoisonReceived?: number
  potionShieldReduction?: number
  doubleStrikeBonus?: number
  doubleStrikePercent?: number
}

interface BattleLogData {
  battleId: string
  player1: {
    address: string
    level: number
    initialEnergy: number
    finalEnergy: number
    buffsUsed: number[] // AGC: Buff IDs selected for battle
    xpEarned: number
    selectedBuffs?: number[] // AGC: Buff IDs selected for battle (alias)
    potions?: number[] // AGC: Potion token IDs selected for battle
  }
  player2: {
    address: string
    level: number
    initialEnergy: number
    finalEnergy: number
    buffsUsed: number[] // AGC: Buff IDs selected for battle
    xpEarned: number
    selectedBuffs?: number[] // AGC: Buff IDs selected for battle (alias)
    potions?: number[] // AGC: Potion token IDs selected for battle
  }
  turns: BattleLogTurn[]
  status: string
  winner: string | null
  isDraw: boolean
  totalTurns: number
  player2IsBot: boolean
}

// AGC: Local helper type describing blockchain-sourced battles for recent feed
interface BlockchainBattleSummary {
  battleId: string
  battleType: 'PVE' | 'PVP'
  outcome: 'WIN' | 'LOSS' | 'DRAW'
  timestamp: number | null
  transactionHash: string | null
  blockNumber: number
  playerAddress: string
  opponentAddress: string | null
  playerLevel: number
  opponentLevel: number
}

// AGC: Limit for the number of recent battles shown in the profile feed
const PROFILE_RECENT_BATTLE_LIMIT = 10

interface ProfileProps {
  playerAddress: string
  onNavigateToPage?: (page: ActivePage) => void
  onNavClick: (nav: BottomNavKey) => void
  activeNav: BottomNavKey
  onOpenSuggestion: () => void
  onOpenSettings: () => void
  onOpenInventory: () => void
}

// AGC: Profile component showing player battle statistics and history
export const Profile: React.FC<ProfileProps> = ({ 
  playerAddress, 
  onNavigateToPage,
}) => {
  const { playerHistory, loading: historyLoading, getBattleStats } = usePlayerHistory(playerAddress)
  const { playerStats } = usePlayerProgression(playerAddress)
  const { stats: blockchainStats, loading: statsLoading } = useBlockchainStats(playerAddress)
  const { address } = useAccount()
  // AGC: ogbBalance removed - unused
  const { balance: _ogbBalance } = usePlayerStatus(address)

  const loading = historyLoading || statsLoading
  const botAddress = (import.meta.env.VITE_BATTLE_BOT_ADDRESS || '0x88e1e984881cbd756a5d6b398b64d6493047b208').toLowerCase()
  const localStats = getBattleStats()
  type BattleRecordWithTx = BattleRecord & { resolutionTxHash?: string }
  const normalizedHistory: PlayerHistory = playerHistory ?? {
    walletAddress: playerAddress,
    totalBattles: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    knockouts: 0,
    totalExperience: 0,
    lastBattleDate: 0,
    battleHistory: [],
  }

  // AGC: State declarations - must come BEFORE useMemo that references them
  const [showPerformance, setShowPerformance] = useState(false)
  const [battleLogData, setBattleLogData] = useState<BattleLogData | null>(null)
  const [battleLogTitle, setBattleLogTitle] = useState<string>('Battle Log')
  const [isBattleLogLoading, setIsBattleLogLoading] = useState(false)
  const [selectedTurnIndex, setSelectedTurnIndex] = useState(-1) // AGC: Start with pre-battle view (-1)
  const [blockchainBattles, setBlockchainBattles] = useState<BlockchainBattleSummary[]>([])
  const [isLoadingBlockchainBattles, setIsLoadingBlockchainBattles] = useState(false)

  // AGC: Unified recent battles feed - merges blockchain and local history, deduplicates, sorts by timestamp
  type ProfileBattleFeedItem =
    | { source: 'blockchain'; data: BlockchainBattleSummary; timestamp: number }
    | { source: 'local'; data: BattleRecordWithTx; timestamp: number }
  const profileRecentBattles = useMemo<ProfileBattleFeedItem[]>(() => {
    const filteredLocalBattles = normalizedHistory.battleHistory.filter((record) => {
      const recordWithTx = record as BattleRecordWithTx
      if (!recordWithTx.resolutionTxHash) return true
      return !blockchainBattles.some(
        (chainBattle) =>
          chainBattle.transactionHash &&
          recordWithTx.resolutionTxHash === chainBattle.transactionHash
      )
    })
    const localFeed = filteredLocalBattles.map((battle) => ({
      source: 'local' as const,
      data: battle as BattleRecordWithTx,
      timestamp: battle.timestamp ?? 0
    }))
    const blockchainFeed = blockchainBattles.map((battle) => ({
      source: 'blockchain' as const,
      data: battle,
      timestamp: battle.timestamp ?? 0
    }))
    return [...blockchainFeed, ...localFeed]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, PROFILE_RECENT_BATTLE_LIMIT)
  }, [blockchainBattles, normalizedHistory.battleHistory])
  const recentBattleLabelCount = Math.min(PROFILE_RECENT_BATTLE_LIMIT, profileRecentBattles.length)

  const safeBlockchainStats = blockchainStats ?? {
    totalBattles: 0,
    winRate: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    totalExperience: 0
  }
  const localBattleCount = normalizedHistory.battleHistory.length
  const totalBattlesOnChain = safeBlockchainStats.totalBattles ?? 0
  const effectiveBattleCount = Math.max(totalBattlesOnChain, localBattleCount)
  const hasAnyBattleHistory = effectiveBattleCount > 0
  const displayedWinRate = totalBattlesOnChain > 0 ? safeBlockchainStats.winRate : localStats.winRate
  const displayedWins = totalBattlesOnChain > 0 ? safeBlockchainStats.wins : normalizedHistory.wins
  const displayedLosses = totalBattlesOnChain > 0 ? safeBlockchainStats.losses : normalizedHistory.losses
  const displayedDraws = totalBattlesOnChain > 0 ? safeBlockchainStats.draws : normalizedHistory.draws
  const displayedTotalExperience = totalBattlesOnChain > 0 ? safeBlockchainStats.totalExperience : normalizedHistory.totalExperience

  const walletShort = `${playerAddress.slice(0, 4)}…${playerAddress.slice(-4)}`
  const playerLevel = playerStats?.level ?? 1
  const attack = playerStats?.attack ?? 0
  const defense = playerStats?.defense ?? 0
  const xpCurrent = playerStats?.experience ?? 0
  const xpToNext = playerStats?.experienceToNext ?? 0
  const xpTarget = xpCurrent + Math.max(0, xpToNext)
  const xpProgress = xpTarget > 0 ? Math.min(1, xpCurrent / xpTarget) : 0
  // AGC: ogbBalanceString removed - unused

  // AGC: Fetch battle transactions from blockchain (using current contract addresses from env.public)
  useEffect(() => {
    if (!playerAddress) return

    const fetchBlockchainBattles = async () => {
      setIsLoadingBlockchainBattles(true)
      try {
        const response = await fetch(buildBackendUrl(`api/battle-transactions/${playerAddress}?limit=20`))
        if (response.ok) {
          const data = await response.json()
          setBlockchainBattles(data.transactions || [])
        } else {
          console.warn('Failed to fetch blockchain battles:', response.statusText)
        }
      } catch (error) {
        console.error('Error fetching blockchain battles:', error)
      } finally {
        setIsLoadingBlockchainBattles(false)
      }
    }

    fetchBlockchainBattles()
  }, [playerAddress])

  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  if (loading) {
    return (
      <main
        className="pb-[88px] px-3 min-h-screen flex items-center justify-center text-white text-sm"
        style={sharedTopPaddingStyle}
      >
        <div className="text-slate-300 animate-pulse">Loading profile…</div>
      </main>
    )
  }

  if (!hasAnyBattleHistory) {
    return (
      <main
        className="pb-[88px] px-3 text-white flex flex-col gap-3"
        style={sharedTopPaddingStyle}
      >
        <PlayerProgressionPanel
          title="Player"
          playerAddress={playerAddress}
          walletAddress={playerAddress}
          fallbackLevel={playerLevel}
        />
        <section className="flex flex-col items-center justify-center gap-2 bg-slate-900/60 border border-slate-800 rounded-2xl p-6 text-center">
          <div className="text-5xl">⚔️</div>
          <h2 className="text-lg font-semibold">No Battles Yet</h2>
          <p className="text-sm text-slate-400">Enter the arena to begin tracking wins, streaks, and XP.</p>
          <button
            onClick={() => onNavigateToPage && onNavigateToPage('quickBattle')}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 py-3 rounded-xl text-sm font-semibold shadow-lg shadow-blue-500/30 hover:scale-[1.01] transition"
          >
            Start Battling
          </button>
        </section>
      </main>
    )
  }

  const primaryStats = [
    { icon: '🏆', label: 'Battles', value: effectiveBattleCount, accent: 'text-sky-300', sub: 'Matches' },
    { icon: '⚔️', label: 'Wins', value: displayedWins, accent: 'text-emerald-300', sub: 'Victories' },
    { icon: '❌', label: 'Losses', value: displayedLosses, accent: 'text-rose-300', sub: 'Defeats' },
    { icon: '⏸️', label: 'Draws', value: displayedDraws, accent: 'text-amber-300', sub: 'Ties' },
  ] as const

  const secondaryStats = [
    { icon: '🔥', label: 'Streak', value: localStats.streak.count, accent: 'text-purple-300', sub: localStats.streak.type === 'none' ? 'No streak' : `${localStats.streak.type}` },
    { icon: '📈', label: 'Win %', value: `${displayedWinRate}%`, accent: 'text-emerald-300', sub: 'Rate' },
    { icon: '💀', label: 'KOs', value: normalizedHistory.knockouts, accent: 'text-orange-300', sub: 'Finishes' },
    { icon: '⚡', label: 'KO %', value: `${localStats.koRate}%`, accent: 'text-yellow-300', sub: 'Rate' },
  ] as const

  const handleOpenBattleLog = async (battleId?: string, description?: string) => {
    if (!battleId) {
      return
    }
    setBattleLogTitle(description ?? 'Battle Log')
    setIsBattleLogLoading(true)
    setBattleLogData(null)
    setSelectedTurnIndex(-1) // AGC: Start with pre-battle view (battle setup)
    try {
      // AGC: Fetch JSON format (not HTML) for React-based rendering
      const response = await fetch(buildBackendUrl(`api/battle/${battleId}/log`))
      if (!response.ok) {
        throw new Error('Failed to fetch battle log')
      }
      const data = await response.json()
      setBattleLogData(data)
    } catch (error) {
      console.error('Error fetching battle log:', error)
      setBattleLogData(null)
    } finally {
      setIsBattleLogLoading(false)
    }
  }

  const handleCloseBattleLog = () => {
    setBattleLogData(null)
    setBattleLogTitle('Battle Log')
    setIsBattleLogLoading(false)
    setSelectedTurnIndex(-1) // AGC: Reset to pre-battle view
  }

  // AGC: Render helper for blockchain-loaded recent battle rows
  const renderBlockchainBattleRow = (battle: BlockchainBattleSummary) => {
    // AGC: Only show "Bot" if opponent address exactly matches VITE_BATTLE_BOT_ADDRESS (not PlayerAI)
    const isBotOpponent = battle.opponentAddress && battle.opponentAddress.toLowerCase() === botAddress
    // AGC: Always show address if available (treat PlayerAI like regular player)
    // AGC: Format address as 0x8d9...51aD (first 5 chars + last 4 chars)
    const opponentShort = battle.opponentAddress
      ? `${battle.opponentAddress.slice(0, 5)}...${battle.opponentAddress.slice(-4)}`
      : ''
    const outcomeColor =
      battle.outcome === 'WIN'
        ? 'text-emerald-400'
        : battle.outcome === 'LOSS'
          ? 'text-rose-400'
          : 'text-amber-300'
    const battleTimestamp = battle.timestamp ?? 0
    return (
      <div key={`blockchain-${battle.battleId}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0 hover:bg-slate-900/30 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className={`text-xs font-semibold ${outcomeColor}`}>
              {battle.outcome}
            </p>
            <span className="text-[9px] text-slate-500">({battle.battleType})</span>
          </div>
          <p className="text-[10px] text-slate-400 truncate">
            {new Date(battleTimestamp).toLocaleDateString()} • {battle.battleType}
          </p>
          <p className="text-[9px] text-slate-500 truncate">
            {isBotOpponent 
              ? `vs Bot Lv ${battle.opponentLevel}` 
              : opponentShort 
                ? `vs Lv ${battle.opponentLevel} (${opponentShort})`
                : `vs Lv ${battle.opponentLevel}`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {battle.battleId && (
            <button
              onClick={() => handleOpenBattleLog(battle.battleId, `${battle.outcome} • ${new Date(battleTimestamp).toLocaleString()}`)}
              className="text-[11px] px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-400/40 text-blue-300 hover:bg-blue-600/30 hover:text-blue-200 transition-colors font-semibold min-w-[50px] text-center"
            >
              LOG
            </button>
          )}
          {battle.transactionHash ? (
            <a
              href={`https://sepolia.basescan.org/tx/${battle.transactionHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] px-3 py-1.5 rounded-lg bg-purple-600/20 border border-purple-400/40 text-purple-300 hover:bg-purple-600/30 hover:text-purple-200 transition-colors font-semibold min-w-[50px] text-center"
            >
              BASE
            </a>
          ) : (
            <span className="text-[10px] text-slate-500 px-3 py-1.5 min-w-[50px] text-center">Pending</span>
          )}
        </div>
      </div>
    )
  }

  // AGC: Render helper for locally stored recent battle rows
  const renderLocalBattleRow = (battle: BattleRecordWithTx) => {
    // AGC: Only show "Bot" if opponent address exactly matches VITE_BATTLE_BOT_ADDRESS (not PlayerAI)
    const isBotOpponent = battle.opponentAddress && battle.opponentAddress.toLowerCase() === botAddress
    // AGC: Always show address if available (treat PlayerAI like regular player)
    // AGC: Format address as 0x8d9...51aD (first 5 chars + last 4 chars)
    const opponentShort = battle.opponentAddress
      ? `${battle.opponentAddress.slice(0, 5)}...${battle.opponentAddress.slice(-4)}`
      : ''
    const outcomeColor =
      battle.outcome === 'WIN'
        ? 'text-emerald-400'
        : battle.outcome === 'LOSS'
          ? 'text-rose-400'
          : 'text-amber-300'
    const battleTimestamp = battle.timestamp ?? 0
    return (
      <div key={`local-${battle.id}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0 hover:bg-slate-900/30 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className={`text-xs font-semibold ${outcomeColor}`}>
              {battle.outcome}
            </p>
            {battle.isKnockout && <span className="text-orange-300 text-[9px]">(KO)</span>}
          </div>
          <p className="text-[10px] text-slate-400 truncate">
            {new Date(battleTimestamp).toLocaleDateString()} • {battle.turns ?? 0} turns • +{battle.experienceGained} XP
          </p>
          <p className="text-[9px] text-slate-500 truncate">
            {isBotOpponent 
              ? `vs Bot Lv ${battle.opponentLevel}` 
              : opponentShort 
                ? `vs Lv ${battle.opponentLevel} (${opponentShort})`
                : `vs Lv ${battle.opponentLevel}`}
          </p>
        </div>
        {battle.battleId && (
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <button
              onClick={() => handleOpenBattleLog(battle.battleId, `${battle.outcome} • ${new Date(battleTimestamp).toLocaleString()}`)}
              className="text-[11px] px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-400/40 text-blue-300 hover:bg-blue-600/30 hover:text-blue-200 transition-colors font-semibold min-w-[50px] text-center"
            >
              LOG
            </button>
            {battle.resolutionTxHash ? (
              <a
                href={`https://sepolia.basescan.org/tx/${battle.resolutionTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] px-3 py-1.5 rounded-lg bg-purple-600/20 border border-purple-400/40 text-purple-300 hover:bg-purple-600/30 hover:text-purple-200 transition-colors font-semibold min-w-[50px] text-center"
              >
                BASE
              </a>
            ) : (
              <span className="text-[10px] text-slate-500 px-3 py-1.5 min-w-[50px] text-center">Pending</span>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PLAYER</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Lv {playerLevel}</h1>
            <p className="text-[11px] text-slate-400">{walletShort} • ATK {attack} • DEF {defense}</p>
          </div>
          <a 
            href={`https://sepolia.basescan.org/address/${playerAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
          >
            BaseScan
          </a>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
            <span>XP Progress</span>
            <span>{xpCurrent.toLocaleString()} / {xpTarget.toLocaleString()}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
              style={{ width: `${xpProgress * 100}%` }}
            />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-4 gap-1.5 text-[11px]">
        {primaryStats.map((tile) => (
          <div
            key={tile.label}
            className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)] hover:bg-slate-900/90 transition-colors"
          >
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>{tile.icon}</span>
              {tile.label}
            </div>
            <div className={`text-2xl font-extrabold mt-0.5 ${tile.accent}`}>{tile.value}</div>
            <div className="text-[9px] text-slate-500">{tile.sub}</div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-4 gap-1.5 text-[11px]">
        {secondaryStats.map((tile) => (
          <div
            key={tile.label}
            className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)] hover:bg-slate-900/90 transition-colors"
          >
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>{tile.icon}</span>
              {tile.label}
            </div>
            <div className={`text-2xl font-extrabold mt-0.5 ${tile.accent}`}>{tile.value}</div>
            <div className="text-[9px] text-slate-500">{tile.sub}</div>
          </div>
        ))}
      </section>

      <section className="text-sm">
        <div className="bg-slate-950/60 border border-slate-900 rounded-2xl p-2.5 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <button
            onClick={() => setShowPerformance((prev) => !prev)}
            className="w-full flex items-center justify-between text-[11px] uppercase text-slate-400"
          >
            <span className="flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-cyan-300" />
              Performance
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showPerformance ? 'rotate-180' : ''}`} />
          </button>
          {showPerformance && (
            <div className="space-y-1.5 mt-2">
              {[
                { label: 'Avg Turns / Win', value: localStats.averageTurnsPerWin, color: 'text-cyan-300' },
                { label: 'Total XP', value: displayedTotalExperience.toLocaleString(), color: 'text-purple-300' },
                { label: 'Tokens Won', value: `${localStats.totalTokensWon} OGB`, color: 'text-emerald-300' },
                { label: 'Tokens Lost', value: `${localStats.totalTokensLost} OGB`, color: 'text-rose-300' },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-300">{row.label}</span>
                  <span className={`font-semibold ${row.color}`}>{row.value}</span>
              </div>
              ))}
            </div>
          )}
          </div>
      </section>

      {/* AGC: Divider between Performance and Recent Battles */}
      <div className="border-b border-white/10"></div>

      {/* AGC: Recent Battles Section - No card, just border divider (following improve_design.md) */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <Flame className="w-4 h-4 text-orange-300" />
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">RECENT BATTLES</p>
          </div>
          <span className="text-[9px] text-slate-500">Last {recentBattleLabelCount}</span>
        </div>
        {isLoadingBlockchainBattles ? (
          <div className="text-center text-slate-400 py-4 text-[11px]">Loading battles from blockchain...</div>
        ) : profileRecentBattles.length > 0 ? (
          <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent pr-1">
            {profileRecentBattles.map((entry) =>
              entry.source === 'blockchain'
                ? renderBlockchainBattleRow(entry.data)
                : renderLocalBattleRow(entry.data)
            )}
          </div>
        ) : (
          <div className="text-center text-slate-400 py-4 text-[11px]">No recent battles</div>
        )}
      </section>
      {/* AGC: Battle Log Modal - React-based rendering with full breakdown */}
      {battleLogData !== null && !isBattleLogLoading && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleCloseBattleLog}
        >
          <div 
            className="w-full max-w-lg bg-slate-950 border border-blue-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* AGC: Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-slate-900">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-blue-400">Battle Log</p>
                <h3 className="text-base font-bold text-white">{battleLogTitle}</h3>
              </div>
              <button
                onClick={handleCloseBattleLog}
                className="p-1.5 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
                aria-label="Close battle log"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* AGC: Turn Navigation - includes pre-battle view (index -1) */}
            <div className="flex items-center justify-between px-4 py-1.5 bg-slate-900/50 border-b border-white/10">
              <button
                onClick={() => setSelectedTurnIndex(Math.max(-1, selectedTurnIndex - 1))}
                disabled={selectedTurnIndex === -1}
                className="px-3 py-1 text-sm bg-slate-800 rounded-lg disabled:opacity-30 hover:bg-slate-700 transition"
              >
                ← Prev
              </button>
              <span className="text-sm text-slate-300">
                {selectedTurnIndex === -1 
                  ? 'Battle Setup' 
                  : `Turn ${selectedTurnIndex + 1} of ${battleLogData.turns.length}`}
              </span>
              <button
                onClick={() => {
                  if (selectedTurnIndex === -1) {
                    setSelectedTurnIndex(0) // AGC: Go from pre-battle to turn 1
                  } else {
                    setSelectedTurnIndex(Math.min(battleLogData.turns.length - 1, selectedTurnIndex + 1))
                  }
                }}
                disabled={selectedTurnIndex >= battleLogData.turns.length - 1}
                className="px-3 py-1 text-sm bg-slate-800 rounded-lg disabled:opacity-30 hover:bg-slate-700 transition"
              >
                Next →
              </button>
            </div>
            
            {/* AGC: Turn Content - Pre-battle view or Turn view */}
            <div className={`flex-1 overflow-y-auto ${selectedTurnIndex === -1 ? 'p-3' : 'p-4'} ${selectedTurnIndex === -1 ? '' : 'space-y-4'}`}>
              {selectedTurnIndex === -1 ? (
                <BattleLogPreBattleView
                  battleData={battleLogData}
                  playerAddress={playerAddress}
                />
              ) : battleLogData.turns.length > 0 && (
                <BattleLogTurnView
                  turn={battleLogData.turns[selectedTurnIndex]}
                  player1Address={battleLogData.player1.address}
                  player2Address={battleLogData.player2.address}
                  player2IsBot={battleLogData.player2IsBot}
                  playerAddress={playerAddress}
                />
              )}
            </div>
            
            {/* AGC: Footer with summary */}
            <div className="px-4 py-2 border-t border-white/10 bg-slate-900">
              <div className="flex items-center justify-between text-xs">
                <span className={battleLogData.isDraw ? 'text-yellow-400' : 
                  battleLogData.winner?.toLowerCase() === playerAddress.toLowerCase() ? 'text-green-400' : 'text-red-400'}>
                  {battleLogData.isDraw ? '⚖️ DRAW' : 
                   battleLogData.winner?.toLowerCase() === playerAddress.toLowerCase() ? '🏆 YOU WON' : '💀 YOU LOST'}
                </span>
                <button
                  onClick={handleCloseBattleLog}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-400 hover:to-purple-400 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isBattleLogLoading && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-slate-300 text-sm animate-pulse">Loading battle log…</div>
        </div>
      )}
    </main>
  )
}

// AGC: Helper functions for battle log rendering (matching BattleTurnModal exactly)
const CHOICE_IMAGE_MAP: Record<string, { src: string; alt: string }> = {
  rock: { src: '/rock.png', alt: 'Rock hand' },
  paper: { src: '/paper.png', alt: 'Paper hand' },
  scissors: { src: '/scissor.png', alt: 'Scissors hand' },
}

const resolveImagePath = (assetPath: string): string => {
  if (!assetPath) return assetPath
  if (/^https?:\/\//i.test(assetPath)) return assetPath
  try {
    const base = import.meta.env?.BASE_URL ?? '/'
    const normalizedBase = typeof base === 'string' && base.endsWith('/') ? base : `${base}/`
    const trimmedPath = assetPath.replace(/^\/+/, '')
    return `${normalizedBase}${trimmedPath}`
  } catch {
    return assetPath
  }
}

const getElementImagePath = (element: string | undefined | null) => {
  const normalizedElement = element?.toLowerCase()
  switch (normalizedElement) {
    case 'water': return '/water.png'
    case 'fire': return '/fire.png'
    case 'ice': return '/ice.png'
    case 'wind': return '/wind.png'
    default: return null
  }
}

const choiceEmoji = (choice?: string | null) => {
  switch ((choice ?? '').toLowerCase()) {
    case 'rock': return '🪨'
    case 'paper': return '📄'
    case 'scissors': return '✂️'
    default: return '❔'
  }
}

const elementEmoji = (element?: string | null) => {
  switch ((element ?? '').toLowerCase()) {
    case 'water': return '💧'
    case 'fire': return '🔥'
    case 'ice': return '❄️'
    case 'wind': return '💨'
    case 'none': return '⚪'
    default: return '—'
  }
}

const formatSigned = (value: number) => {
  const rounded = Math.abs(Math.round(value))
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${rounded}`
}

const numberOrDash = (value?: number) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return Math.round(value)
  }
  return '—'
}

// AGC: Determine element relationship based on Water > Fire > Ice > Wind > Water cycle
const determineElementRelationship = (attackerElement: string | null | undefined, defenderElement: string | null | undefined): 'WON' | 'LOST' | 'NEUTRAL' | 'DRAW' => {
  if (!attackerElement || !defenderElement) {
    return 'NEUTRAL'
  }
  
  const attacker = attackerElement.toUpperCase()
  const defender = defenderElement.toUpperCase()
  
  // AGC: Same element = DRAW
  if (attacker === defender) {
    return 'DRAW'
  }
  
  // AGC: Element advantage cycle: Water > Fire > Ice > Wind > Water
  const advantages: Record<string, string> = {
    WATER: 'FIRE',
    FIRE: 'ICE',
    ICE: 'WIND',
    WIND: 'WATER'
  }
  
  // AGC: Check if attacker beats defender
  if (advantages[attacker] === defender) {
    return 'WON'
  }
  
  // AGC: Check if defender beats attacker
  if (advantages[defender] === attacker) {
    return 'LOST'
  }
  
  // AGC: Non-adjacent elements (neither beats the other) = NEUTRAL
  return 'NEUTRAL'
}

// AGC: FIX 2025-11-29 - Build base summary matching BattleTurnModal format exactly
const buildBaseSummary = (breakdown: TurnBreakdown, attackerElement?: string, defenderElement?: string) => {
  const base = Math.round(breakdown.baseDamage ?? 0)
  const level = Math.round(breakdown.levelBonus ?? 0)
  const element = Math.round(breakdown.elementBonus ?? 0)
  
  // AGC: Determine HERO status from baseName (WIN/LOSE/DRAW or choice name)
  const baseNameUpper = (breakdown.baseName ?? '').toUpperCase()
  let heroStatus = 'DRAW'
  if (baseNameUpper.includes('WIN')) {
    heroStatus = 'WIN'
  } else if (baseNameUpper.includes('LOSE') || baseNameUpper.includes('LOST')) {
    heroStatus = 'LOSE'
  } else if (baseNameUpper.includes('DRAW')) {
    heroStatus = 'DRAW'
  }
  
  // AGC: Determine ELEMENT status from element relationship
  const elementRelationship = determineElementRelationship(attackerElement, defenderElement)
  let elementStatus = elementRelationship
  let elementValue = '0'
  
  if (elementRelationship === 'WON') {
    elementValue = `+${Math.abs(element)}`
  } else if (elementRelationship === 'LOST') {
    elementValue = `-${Math.abs(element)}`
  } else if (elementRelationship === 'NEUTRAL') {
    elementValue = `-${Math.abs(element)}`
  } else if (elementRelationship === 'DRAW') {
    elementValue = '0'
  } else {
    // AGC: Fallback to actual element bonus value if relationship can't be determined
    elementValue = element !== 0 ? (element > 0 ? `+${element}` : `${element}`) : '0'
  }
  
  const levelPart = level !== 0 ? ` + Level ${level}` : ''
  const elementPart = ` + ELEMENT: (${elementStatus}) ${elementValue}`
  const total = base + level + element

  return {
    summary: `HERO: (${heroStatus}) ${base}${levelPart}${elementPart} = ${total}`,
    baseTotal: total,
  }
}

// AGC: FIX 2025-11-29 - Build rarity summary matching BattleTurnModal format
const buildRaritySummary = (breakdown: TurnBreakdown, currentTotal: number) => {
  const rarityAmount = Math.round(breakdown.rarityBonus ?? 0)
  const rarityName = (breakdown.rarityName ?? 'none').toUpperCase()
  if (rarityAmount === 0) {
    return {
      summary: rarityName === 'NONE' ? 'none' : rarityName,
      total: currentTotal,
    }
  }
  const total = currentTotal + rarityAmount
  return {
    summary: `${rarityName} ${formatSigned(rarityAmount)} = ${total}`,
    total,
  }
}

// AGC: FIX 2025-11-29 - Build buff summary with percentage matching BattleTurnModal format
const buildBuffSummary = (
  breakdown: TurnBreakdown,
  baseTotal: number,
  buffs?: Array<{ label: string; status?: 'active' | 'ready' | 'used' }>,
) => {
  // AGC: Show buff as percentage multiplier when mana is active
  const pierce = Math.round(breakdown.pierceReduction ?? 0)
  const haste = Math.round(breakdown.hasteBonus ?? 0)
  const manaBoost = breakdown.manaBoost && breakdown.manaBoost > 1.0 ? breakdown.manaBoost : null
  const contributions: string[] = []
  let runningTotal = baseTotal

  if (pierce !== 0) {
    runningTotal += pierce
    // AGC: Calculate percentage for PIERCE (base 25%, boosted by mana)
    if (manaBoost && baseTotal > 0) {
      const basePiercePercent = 25
      const manaPercent = Math.round((manaBoost - 1.0) * 100)
      const totalPercent = basePiercePercent + manaPercent
      contributions.push(`PIERCE (${basePiercePercent}%+${manaPercent}% Mana = ${totalPercent}%) ${formatSigned(pierce)} ⚡`)
    } else if (baseTotal > 0) {
      contributions.push(`PIERCE (25%) ${formatSigned(pierce)} ⚡`)
    } else {
      contributions.push(`${formatSigned(pierce)} from PIERCE ⚡`)
    }
  }

  if (haste !== 0) {
    runningTotal += haste
    // AGC: Calculate percentage for HASTE (flat +5, boosted by mana)
    if (manaBoost) {
      const manaPercent = Math.round((manaBoost - 1.0) * 100)
      contributions.push(`HASTE (+${manaPercent}% Mana) ${formatSigned(haste)} ⚡`)
    } else {
      contributions.push(`HASTE ${formatSigned(haste)} ⚡`)
    }
  }

  const highlightedBuffs =
    buffs
      ?.filter((buff) => buff.status === 'active' || buff.status === 'used')
      .map((buff) => buff.label.toUpperCase()) ?? []

  const numericSegment = contributions.length > 0 ? `${contributions.join(' + ')} = ${runningTotal}` : null
  const labelSegment = highlightedBuffs.length > 0 && contributions.length === 0 ? highlightedBuffs.join(', ') : null
  const summarySegments = [labelSegment, numericSegment].filter(Boolean)

  return {
    summary: summarySegments.length > 0 ? summarySegments.join(' | ') : 'none',
    total: runningTotal,
  }
}

const buildSpecialEffectLines = (type: 'you' | 'opponent', breakdown: TurnBreakdown, otherBreakdown?: TurnBreakdown) => {
  const messages: Array<{ text: string; tone: 'danger' | 'info' | 'success' }> = []

  // AGC: Mana potion boost - show in YOUR breakdown EVERY TURN it's active
  if (type === 'you' && otherBreakdown?.manaBoost && otherBreakdown.manaBoost > 1.0) {
    const manaPercent = ((otherBreakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
    // AGC: Check if YOUR buff was activated this turn
    const buffWasActivated = otherBreakdown?.shieldReduction || breakdown?.pierceReduction
    const message = buffWasActivated
      ? `💧 Mana Potion boosting your buff! +${manaPercent}%`
      : `💧 Mana Potion active! +${manaPercent}% on next buff`
    messages.push({ text: message, tone: 'success' })
  }

  // AGC: Shield effect
  if (type === 'opponent' && breakdown.shieldReduction && breakdown.shieldReduction > 0) {
    const manaPercent = breakdown.manaBoost && breakdown.manaBoost > 1.0 
      ? ((breakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '') : null
    const manaNote = manaPercent ? ` (+${manaPercent}% Mana)` : ''
    messages.push({ text: `Blocked by your SHIELD! -${Math.round(breakdown.shieldReduction)} damage${manaNote}`, tone: 'info' })
  }
  if (type === 'you' && breakdown.shieldReduction && breakdown.shieldReduction > 0) {
    const manaPercent = breakdown.manaBoost && breakdown.manaBoost > 1.0 
      ? ((breakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '') : null
    const manaNote = manaPercent ? ` (+${manaPercent}% Mana)` : ''
    messages.push({ text: `Blocked by opponent's shield -${Math.round(breakdown.shieldReduction)} damage${manaNote}`, tone: 'danger' })
  }

  // AGC: Frost effect
  if (type === 'you' && breakdown.frostReduction && breakdown.frostReduction > 0) {
    messages.push({ text: `Frozen by opponent! -${Math.round(breakdown.frostReduction)} damage ❄️`, tone: 'danger' })
  }
  if (type === 'opponent' && breakdown.frostReduction && breakdown.frostReduction > 0) {
    messages.push({ text: `Frozen by you! -${Math.round(breakdown.frostReduction)} damage ❄️`, tone: 'info' })
  }

  // AGC: Potion effects
  if (breakdown.potionHealing && breakdown.potionHealing > 0) {
    messages.push({ text: `🧪 Healing Potion! +${Math.round(breakdown.potionHealing)} energy`, tone: 'success' })
  }
  if (breakdown.potionPoisonReceived && breakdown.potionPoisonReceived > 0) {
    const poisonDmg = breakdown.potionPoisonReceived % 1 === 0 
      ? Math.round(breakdown.potionPoisonReceived) : breakdown.potionPoisonReceived.toFixed(1)
    messages.push({ text: `☠️ Poisoned! -${poisonDmg} energy`, tone: 'danger' })
  }
  if (breakdown.potionShieldReduction && breakdown.potionShieldReduction > 0) {
    messages.push({ text: `🧪 Shield Potion! -${Math.round(breakdown.potionShieldReduction)} damage blocked`, tone: 'info' })
  }
  // AGC: Double Strike potion effect (NEW - 2025-11-27)
  if (breakdown.doubleStrikeBonus && breakdown.doubleStrikeBonus > 0) {
    messages.push({ text: `⚔️ Double Strike! +${Math.round(breakdown.doubleStrikeBonus)} damage (+${breakdown.doubleStrikePercent || 25}%)`, tone: 'success' })
  }

  return messages
}

// AGC: Pre-Battle View Component - Shows initial battle setup
interface BattleLogPreBattleViewProps {
  battleData: BattleLogData
  playerAddress: string
}

const BattleLogPreBattleView: React.FC<BattleLogPreBattleViewProps> = ({
  battleData,
  playerAddress
}) => {
  const isPlayer1 = playerAddress.toLowerCase() === battleData.player1.address.toLowerCase()
  const playerData = isPlayer1 ? battleData.player1 : battleData.player2
  const opponentData = isPlayer1 ? battleData.player2 : battleData.player1
  const opponentLabel = battleData.player2IsBot ? 'Bot' : 'Player 2'

  const BUFF_NAMES: Record<number, string> = {
    0: 'Shield',
    1: 'Pierce',
    2: 'Haste',
    3: 'Frost'
  }

  const POTION_NAMES: Record<number, string> = {
    0: 'Healing',
    1: 'Mana',
    2: 'Double Strike',
    3: 'Poison'
  }

  const formatAddress = (addr: string) => {
    if (!addr) return 'N/A'
    // AGC: Format address as 0x8d9...51aD (first 5 chars + last 4 chars)
    return `${addr.slice(0, 5)}...${addr.slice(-4)}`
  }

  const renderPlayerInfo = (
    label: string,
    data: typeof battleData.player1,
    isYou: boolean
  ) => {
    const selectedBuffs = data.selectedBuffs || data.buffsUsed || []
    const selectedPotions = data.potions || []

    return (
      <div className={`rounded-xl p-3 border ${isYou ? 'bg-green-900/20 border-green-500/30' : 'bg-red-900/20 border-red-500/30'}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className={`text-sm font-bold ${isYou ? 'text-green-400' : 'text-red-400'}`}>{label}</h4>
          {isYou && <span className="text-[10px] text-green-300">YOU</span>}
        </div>

        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Wallet:</span>
            <span className="text-white font-mono text-xs">{formatAddress(data.address)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Level:</span>
            <span className="text-white font-bold">Lv {data.level}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Energy:</span>
            <span className="text-white">{data.initialEnergy}/100</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Buffs Selected:</span>
            <span className="text-white">
              {selectedBuffs.length > 0 
                ? selectedBuffs.map(id => BUFF_NAMES[id] || `Buff ${id}`).join(', ')
                : 'None'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Potions Selected:</span>
            <span className="text-white">
              {selectedPotions.length > 0 
                ? selectedPotions.map(id => `#${id}`).join(', ')
                : 'None'}
            </span>
          </div>
          <div className="flex items-center justify-between border-t border-white/10 pt-1.5">
            <span className="text-slate-400">XP Won:</span>
            <span className="text-emerald-400 font-bold">+{data.xpEarned}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      {/* AGC: Removed "Battle Setup" heading - shown in navigation */}
      {renderPlayerInfo('Player 1', battleData.player1, isPlayer1)}
      <div className="text-center text-base font-bold text-slate-400 py-0.5">VS</div>
      {renderPlayerInfo(opponentLabel, battleData.player2, !isPlayer1)}
    </div>
  )
}

// AGC: Turn View Component for battle log
interface BattleLogTurnViewProps {
  turn: BattleLogTurn
  player1Address: string
  player2Address: string
  player2IsBot: boolean
  playerAddress: string
}

const BattleLogTurnView: React.FC<BattleLogTurnViewProps> = ({
  turn,
  player1Address,
  player2Address,
  player2IsBot,
  playerAddress
}) => {
  // AGC: Determine which player is "you" and which is "opponent"
  const isPlayer1 = playerAddress.toLowerCase() === player1Address.toLowerCase()
  const youData = isPlayer1 ? turn.player1 : turn.player2
  const opponentData = isPlayer1 ? turn.player2 : turn.player1
  const opponentLabel = player2IsBot ? 'Bot' : 'Opponent'

  const renderChoiceVisual = (choice?: string | null) => {
    const normalized = (choice ?? '').trim().toLowerCase()
    const mapping = CHOICE_IMAGE_MAP[normalized]
    if (!mapping) {
      return <p className="text-4xl">{choiceEmoji(choice)}</p>
    }
    return (
      <img
        src={resolveImagePath(mapping.src)}
        alt={mapping.alt}
        className="h-10 w-10 object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.45)] select-none pointer-events-none"
      />
    )
  }

  const BUFF_NAMES: Record<number, string> = {
    0: 'Shield',
    1: 'Pierce',
    2: 'Haste',
    3: 'Frost'
  }

  const BUFF_IMAGES: Record<number, string> = {
    0: '/shield.png',
    1: '/pierce.png',
    2: '/haste.png',
    3: '/frost.png'
  }

  const renderBuffChips = (
    selectedBuffs?: number[], 
    activatedBuffs?: string[], 
    buffStatus?: Array<{ name: string; status: string }>,
    frostUsed?: boolean
  ) => {
    if (!selectedBuffs || selectedBuffs.length === 0) {
      return <p className="text-[9px] text-slate-400">None</p>
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {selectedBuffs.map((buffId, index) => {
          const buffName = BUFF_NAMES[buffId] || `Buff ${buffId}`
          const buffImage = BUFF_IMAGES[buffId]
          
          // AGC: Determine buff status
          let status = 'ready'
          const isActivated = activatedBuffs?.some(ab => ab.toUpperCase() === buffName.toUpperCase())
          const statusEntry = buffStatus?.find(bs => bs.name.toUpperCase() === buffName.toUpperCase())
          
          // AGC: Determine status based on activation and usage
          if (buffId === 3 && frostUsed) {
            // AGC: Frost is grey if used previously (disabled)
            status = 'used'
          } else if (isActivated || statusEntry?.status === 'activated') {
            // AGC: Buff activated successfully
            status = 'active'
          } else if (statusEntry?.status === 'failed' || statusEntry?.status === 'missed') {
            // AGC: Buff failed to activate
            status = 'failed'
          } else {
            // AGC: Buff not activated yet (red)
            status = 'ready'
          }
          
          const color = status === 'active'
            ? 'bg-green-600 text-white'
            : status === 'used'
            ? 'bg-slate-600 text-slate-300'
            : 'bg-red-700 text-red-100'
          
          return (
            <span key={`${buffId}-${index}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${color}`}>
              {buffImage && <img src={buffImage} alt={buffName} className="w-3 h-3 object-contain" />}
              <span>{buffName}</span>
            </span>
          )
        })}
      </div>
    )
  }

  const POTION_NAMES: Record<number, string> = {
    0: 'Healing',
    1: 'Mana',
    2: 'Double Strike',
    3: 'Poison'
  }

  const renderPotionChips = (potions?: number[]) => {
    if (!potions || potions.length === 0) {
      return <p className="text-[9px] text-slate-400">None</p>
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {potions.map((tokenId, index) => (
          <span key={`potion-${tokenId}-${index}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-600/80 text-white">
            <span>🧪</span>
            <span>#{tokenId}</span>
          </span>
        ))}
      </div>
    )
  }

  const renderRarityChip = (rarityName?: string | null) => {
    const classes = getRarityBadgeClasses(rarityName)
    if (!classes) return null
    const label = formatRarityLabel(rarityName)
    return (
      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${classes}`}>
        {label}
      </span>
    )
  }

  const clampPercentage = (value?: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return 0
    return Math.max(0, Math.min(100, value))
  }

  const getEnergyBarClass = (value?: number) => {
    if (typeof value !== 'number') return 'bg-red-500'
    if (value >= 75) return 'bg-emerald-400'
    if (value >= 50) return 'bg-yellow-300'
    if (value >= 25) return 'bg-orange-400'
    return 'bg-red-500'
  }

  const renderPlayerCard = (
    label: string,
    data: typeof turn.player1,
    type: 'you' | 'opponent',
    otherData?: typeof turn.player1
  ) => {
    const breakdown = data.breakdown
    const otherBreakdown = otherData?.breakdown
    const specialMessages = breakdown ? buildSpecialEffectLines(type, breakdown, otherBreakdown) : []
    const rarityChip = renderRarityChip(breakdown?.rarityName)

    // AGC: Calculate turn winner
    const yourDamage = type === 'you' ? (data.damage ?? 0) : (otherData?.damage ?? 0)
    const oppDamage = type === 'you' ? (otherData?.damage ?? 0) : (data.damage ?? 0)
    const turnWon = yourDamage > oppDamage
    const turnLost = yourDamage < oppDamage
    const turnDraw = yourDamage === oppDamage && yourDamage > 0

    const selectedBuffs = data.buffs || [] // AGC: Selected buff IDs
    const activatedBuffs = data.activatedBuffs || [] // AGC: Activated buff names
    const buffStatus = data.buffStatus || [] // AGC: Buff status array
    const potions = data.potions || [] // AGC: Potions used this turn
    
    return (
      <div className="bg-slate-900 rounded-xl p-3 space-y-1.5 border border-slate-700">
        {/* AGC: Header with label and energy */}
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-white">{label}</h4>
          <span className="text-xs text-slate-300 font-semibold">Energy: {numberOrDash(data.energyAfter || data.energy)}</span>
        </div>
        
        {/* AGC: Energy bar */}
        <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getEnergyBarClass(data.energyAfter || data.energy)}`}
            style={{ width: `${clampPercentage(data.energyAfter || data.energy)}%` }}
          ></div>
        </div>

        {/* AGC: Compact line: Buffs + Choices = Damage */}
        <div className="flex items-center justify-between gap-2 py-1">
          {/* Left: Buffs */}
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <p className="text-[9px] text-slate-400 whitespace-nowrap">Buffs:</p>
            <div className="flex-1 min-w-0">
              {renderBuffChips(selectedBuffs, activatedBuffs, buffStatus, data.frostUsed)}
            </div>
          </div>
          
          {/* Middle: Choices */}
          <div className="flex items-center gap-1.5">
            {renderChoiceVisual(data.choice)}
            {getElementImagePath(data.element) ? (
              <img
                src={resolveImagePath(getElementImagePath(data.element)!)}
                alt={data.element || 'element'}
                className="w-8 h-8 object-contain"
              />
            ) : (
              <p className="text-xl">{elementEmoji(data.element)}</p>
            )}
          </div>
          
          {/* Right: Damage */}
          <div className="text-right">
            <p className="text-white font-bold text-lg">={numberOrDash(data.damage)}</p>
            <p className="text-[8px] text-slate-400">dmg</p>
          </div>
        </div>

        {/* AGC: Potions used this turn */}
        {potions.length > 0 && (
          <div className="flex items-center gap-2 pt-1 border-t border-white/5">
            <p className="text-[9px] text-slate-400 whitespace-nowrap">Potions:</p>
            {renderPotionChips(potions)}
          </div>
        )}

        {/* AGC: Rarity - inline if present */}
        {rarityChip && (
          <div className="flex items-center gap-2 pt-0.5">
            <p className="text-[9px] text-slate-400 whitespace-nowrap">Rarity:</p>
            {rarityChip}
          </div>
        )}

        {/* AGC: Breakdown details - Matching BattleTurnModal format exactly */}
        {breakdown && (() => {
          // AGC: Get element from data for proper relationship calculation
          const yourElement = type === 'you' ? data.element : otherData?.element
          const oppElement = type === 'you' ? otherData?.element : data.element
          
          // AGC: Convert activatedBuffs to buffs array format for buildBuffSummary
          const buffsArray = data.activatedBuffs?.map(buff => ({
            label: buff,
            status: 'active' as const
          })) || []
          
          // AGC: Build summaries in correct order: HERO → RARITY → BUFF
          const { summary: baseLine, baseTotal } = buildBaseSummary(breakdown, yourElement, oppElement)
          const { summary: raritySummary, total: rarityTotal } = buildRaritySummary(breakdown, baseTotal)
          const { summary: buffSummary, total: buffTotal } = buildBuffSummary(breakdown, rarityTotal, buffsArray)
          
          // AGC: Defense calculation
          const defenseLabel = type === 'you' ? 'Opp Def' : 'Your Def'
          const defenseMultiplier = breakdown.defense ? breakdown.defense.toFixed(2) : '1.00'
          const defenseValue = Math.round(breakdown.afterDefense ?? buffTotal * (breakdown.defense ?? 1))
          const finalDamageValue = Math.round(
            typeof data.damage === 'number'
              ? data.damage
              : breakdown.finalDamage ?? breakdown.afterDefense ?? defenseValue
          )

          return (
            <div className="bg-slate-800 rounded-lg p-2 space-y-0.5 text-[10px]">
              <p className="text-slate-100 leading-tight">{baseLine}</p>
              <p className="text-slate-200 leading-tight">Rarity: {raritySummary}</p>
              <p className="text-slate-200 leading-tight">Buff: {buffSummary}</p>
              <p className="text-slate-300 leading-tight">
                {defenseLabel} (×{defenseMultiplier}) = {defenseValue}
              </p>
              {specialMessages.map((message, idx) => (
                <p key={`${message.text}-${idx}`} className={`leading-tight ${
                  message.tone === 'danger' ? 'text-red-300' : 
                  message.tone === 'success' ? 'text-green-300' : 
                  'text-blue-300'
                }`}>
                  {message.text}
                </p>
              ))}
              <p className={`font-bold leading-tight ${type === 'you' ? 'text-green-300' : 'text-red-300'}`}>Final: {finalDamageValue} dmg</p>
            </div>
          )
        })()}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* AGC: Removed "Turn X" heading - already shown in navigation */}
      {renderPlayerCard('YOU', youData, 'you', opponentData)}
      {renderPlayerCard(opponentLabel.toUpperCase(), opponentData, 'opponent', youData)}
    </div>
  )
}

